/**
 * 2026-07-27 — the per-studio connector activate/deactivate vocabulary.
 *
 * A STANDALONE, dependency-free module (like `secret-kinds.ts`) rather than
 * part of `l2-yaml.ts`: the dashboard SPA renders the Connectors section from
 * {@link CONNECTOR_DESCRIPTORS}, and `l2-yaml.ts` reaches for `node:fs` — it
 * can never enter a browser bundle. `l2-yaml.ts` re-exports everything here,
 * so the config schema keeps its single import surface.
 *
 * Deactivating a connector is a FULL pause in both directions: the outbound
 * canonical actions refuse with `connector_disabled`, the vendor's inbound
 * webhooks are acknowledged (HTTP 200) but skipped, and its cron scans stop.
 * The flag lives on the per-studio `studio_config` live override, so a
 * dashboard Save pauses a vendor with NO redeploy — the point being that
 * pausing is an incident/migration lever, not a deploy.
 */

/**
 * The connectors an operator can activate/deactivate per studio.
 *
 * Stripe is split per-ENVIRONMENT because one studio can face two Stripe
 * accounts at once (E-Pulsive: the LIVE CodexFit account whose legacy
 * subscriptions arrive as ingress, and the TEST/CRM account outbound
 * checkout runs on). Both POST to the single `/webhooks/stripe/<slug>`
 * endpoint and are told apart by Stripe's own `event.livemode` stamp, so two
 * keys are the only way to pause one stream without the other.
 *
 * Deliberately EXCLUDED: `telegram` (Layer's internal PA/staff-alert channel,
 * not a studio-facing vendor surface), `transcription` (an env-keyed inbound
 * enrichment helper with no ingress route or cron of its own) and
 * `website_analytics` (already self-gates on the presence of its config
 * block). `google_ads` (2026-10-06) also needs its config block, but it SENDS
 * purchases to Google, so it gets a pause. Since 2026-10-07 the same pause
 * also stops the campaign changes an approved ads card makes.
 */
export type ConnectorKey =
  | "stripe_live"
  | "stripe_test"
  | "klaviyo"
  | "resend"
  | "whatsapp"
  | "gmail"
  | "instagram"
  | "webchat"
  | "google_ads";

/** Every connector key, in dashboard display order. */
export const CONNECTOR_KEYS: readonly ConnectorKey[] = [
  "stripe_live",
  "stripe_test",
  "klaviyo",
  "resend",
  "whatsapp",
  "gmail",
  "instagram",
  "webchat",
  "google_ads",
];

/** Read-only `Set` view for cheap membership tests in validators. */
export const CONNECTOR_KEYS_SET: ReadonlySet<string> = new Set(CONNECTOR_KEYS);

/**
 * Display metadata for one connector. Lives here rather than in the SPA so
 * the Connectors section can never drift behind an added connector — the
 * same lesson the vendor-credentials section learned by hand-copying
 * `KNOWN_SECRET_KINDS` and silently dropping eleven kinds.
 */
export interface ConnectorDescriptor {
  readonly key: ConnectorKey;
  /** Row label in the dashboard Connectors section. */
  readonly label: string;
  /** One line on what deactivating this connector actually stops. */
  readonly pauses: string;
  /**
   * Present ONLY on the three conversational connectors (WhatsApp, Instagram,
   * Gmail). Its presence is what makes the dashboard render a four-state
   * radio row instead of a plain on/off checkbox — see
   * {@link ConnectorAiDescriptor}.
   */
  readonly ai?: ConnectorAiDescriptor;
  /**
   * 2026-09-01 — present ONLY on `webchat`, the capability-only chat surface:
   * a member who opened the site's "Ask a question" box is talking to the
   * assistant BY CHOICE, so the autonomy ladder ("which inbound does it
   * engage with") is meaningless there — the only real dials are on/off and
   * the capability pair. Its presence makes the dashboard render the
   * three-state chat row (Off / Answer only / Answer and act). Off rides the
   * EXISTING `studio.webchat_enabled` gate the member-api endpoint enforces —
   * NOT `connectors.webchat.enabled`, which is never written (two off
   * switches for one surface is the drift this comment exists to prevent).
   * Mutually exclusive with `ai` by construction.
   */
  readonly chat?: ConnectorChatDescriptor;
}

// ---------------------------------------------------------------------------
// AI autonomy — how far the assistant may go on a conversational connector.
// ---------------------------------------------------------------------------

/**
 * How far the assistant may go WITHOUT a human opting a person in, ordered by
 * increasing autonomy. One vocabulary across all three conversational
 * connectors (2026-08-06; generalised from the Instagram-only ladder shipped
 * hours earlier the same day).
 *
 *   - `notify`         — the assistant never replies on its own. Inbound is
 *                        recorded and staff are alerted; a human opts one
 *                        person in from their member page.
 *   - `booking_intent` — the assistant takes a conversation over ONLY when
 *                        `classify_inbound_dm` reads booking/pricing intent.
 *                        Press, partners, complaints still just notify. The
 *                        post-Phoebe-Parke design (2026-07-24).
 *   - `all`            — the assistant answers EVERY inbound. No classifier
 *                        runs. Deliberately the loudest setting.
 *
 * NOT a direction axis. An inbound/outbound split was considered and rejected
 * (Mario, 2026-08-06): the connector flag already stops both directions, and
 * of the four direction states one duplicates `notify` and one — outbound
 * without inbound — is a bot with nothing to answer.
 */
export const AI_AUTONOMY_MODES = ["notify", "booking_intent", "all"] as const;

export type AiAutonomyMode = (typeof AI_AUTONOMY_MODES)[number];

/** Read-only `Set` view for cheap membership tests in validators. */
export const AI_AUTONOMY_MODES_SET: ReadonlySet<string> = new Set(
  AI_AUTONOMY_MODES,
);

/**
 * The CAPABILITY axis (2026-08-14) — orthogonal to `autonomy`. Autonomy
 * answers "WHICH inbound messages does the assistant engage with"; capability
 * answers "and once engaged, WHAT may it do":
 *
 *   - `converse` — replies and looks things up, but cannot act for the
 *                  member: no bookings, reschedules, cancellations, payment
 *                  links, or profile changes. Hands those to staff via
 *                  `escalate_to_staff`.
 *   - `transact` — replies AND acts (books, reschedules, takes payment in
 *                  the chat). What WhatsApp/Instagram have always done.
 *
 * Deliberately a SECOND field, not a rung on the autonomy ladder: folding it
 * in would have re-interpreted the live `'all'` (which acts today) and needed
 * a data migration. Enforced as member-tier tool-allowlist narrowing in the
 * chat-translator saga — never as prompt copy alone.
 */
export const CONNECTOR_CAPABILITIES = ["converse", "transact"] as const;

export type ConnectorCapability = (typeof CONNECTOR_CAPABILITIES)[number];

/** Read-only `Set` view for cheap membership tests in validators. */
export const CONNECTOR_CAPABILITIES_SET: ReadonlySet<string> = new Set(
  CONNECTOR_CAPABILITIES,
);

/** Operator-facing copy for one option of a connector's capability pair. */
export interface ConnectorCapabilityOption {
  readonly value: ConnectorCapability;
  /** Radio label. */
  readonly label: string;
  /** One line under the label, in the operator's terms. */
  readonly detail: string;
}

/** Operator-facing copy for one rung of a connector's autonomy ladder. */
export interface ConnectorAiOption {
  readonly mode: AiAutonomyMode;
  /** Radio label. */
  readonly label: string;
  /** One line under the label, in the operator's terms. */
  readonly detail: string;
  /** Renders a warning chip — reserved for the unguarded top rung. */
  readonly danger?: boolean;
}

/**
 * A connector's AI ladder: its per-channel default plus the operator copy.
 *
 * `defaultMode` is deliberately PER CONNECTOR and not a single shared
 * constant. A uniform default is the trap here: WhatsApp's assistant answers
 * every inbound today (there has never been a studio-level gate on it — the
 * only kill switch was pausing the whole connector, which also silences staff
 * alerts, since they share it). Defaulting WhatsApp to anything but `all`
 * would switch the live bot off the moment this shipped, with no operator
 * action and no error. Instagram defaults to `notify` (what it shipped with);
 * Gmail defaults to `notify` because its lane is new and must stay off until
 * it is deliberately turned on.
 */
export interface ConnectorAiDescriptor {
  readonly defaultMode: AiAutonomyMode;
  /** The ladder in display order, least autonomous first. */
  readonly options: readonly ConnectorAiOption[];
  /**
   * Default for the capability axis — PER CONNECTOR for the same reason as
   * `defaultMode`: WhatsApp/Instagram have always transacted, so anything but
   * `transact` there would strip the live bot's booking tools on deploy with
   * no operator action. Gmail defaults to `converse` while its reply lane is
   * new: answering is opt-in per member, acting is a deliberate /config Save.
   */
  readonly defaultCapability: ConnectorCapability;
  /** The capability pair in display order, least capable first. */
  readonly capabilityOptions: readonly ConnectorCapabilityOption[];
  /**
   * The exact name of this channel's per-member switch as the member page
   * renders it ("Email AI", not "Gmail AI"). Single source for BOTH the
   * config-page explainer and the member-page pill label — the two drifted
   * apart when each carried its own copy (config said "Gmail AI", member page
   * said "Email AI"; 2026-08-14).
   */
  readonly memberSwitchLabel: string;
}

/**
 * A capability-only chat surface's descriptor (2026-09-01; today only
 * `webchat`). Deliberately NOT a {@link ConnectorAiDescriptor} with an empty
 * ladder: it has no autonomy axis at all (the member opened the chat — there
 * is no "which inbound does it engage with" question), and no per-member
 * switch (webchat sessions bind by email lookup, not a member-page pill), so
 * neither `options`/`defaultMode` nor `memberSwitchLabel` would be honest
 * fields to carry.
 */
export interface ConnectorChatDescriptor {
  /**
   * Default for the capability axis. `transact` for webchat — the widget has
   * booked and taken payment since phase 88, so anything else would strip the
   * live chat's tools on deploy with no operator action (the same
   * preserve-live-behaviour rule as {@link ConnectorAiDescriptor.defaultCapability}).
   */
  readonly defaultCapability: ConnectorCapability;
  /** The capability pair in display order, least capable first. */
  readonly capabilityOptions: readonly ConnectorCapabilityOption[];
  /**
   * The one-line footer under the row — states what these dials do NOT touch
   * (the site's own booking/payment flows), so the row can never read as a
   * kill switch for bookings.
   */
  readonly note: string;
}

/** Connector rows the dashboard renders, in display order. */
export const CONNECTOR_DESCRIPTORS: readonly ConnectorDescriptor[] = [
  {
    key: "stripe_live",
    label: "Stripe · Live (CodexFit account)",
    pauses:
      "Inbound webhooks from the live Stripe account (legacy payments and " +
      "subscriptions). Outbound charges are governed by the outbound " +
      "environment below, which defaults to test.",
  },
  {
    key: "stripe_test",
    label: "Stripe · Test (sandbox account)",
    pauses:
      "Inbound webhooks from the sandbox account, and — while the outbound " +
      "environment is 'test' (the default) — every outbound charge, refund " +
      "and Checkout Session.",
  },
  {
    key: "klaviyo",
    label: "Klaviyo",
    pauses:
      "Inbound only — Klaviyo webhooks (member upserts, booking mirrors). " +
      "Clavis makes no outbound Klaviyo calls.",
  },
  {
    key: "resend",
    label: "Resend (email)",
    pauses: "Outbound email sends and inbound Resend engagement events.",
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    pauses:
      "Inbound member messages and every outbound WhatsApp send — which " +
      "includes staff alerts, since they share this connector. Meta's " +
      "verification handshake stays live so the subscription is not dropped.",
    ai: {
      // `all` = today's live behaviour. See ConnectorAiDescriptor.
      defaultMode: "all",
      defaultCapability: "transact",
      capabilityOptions: [
        {
          value: "converse",
          label: "Answer only",
          detail:
            "Replies and helps, but cannot act: no bookings, reschedules, " +
            "cancellations or payment links. It offers to hand those to the " +
            "team and alerts staff.",
        },
        {
          value: "transact",
          label: "Answer and act",
          detail:
            "Replies and acts for the member: books and reschedules " +
            "sessions and takes payment in the chat. Today's behaviour, and " +
            "the default.",
        },
      ],
      memberSwitchLabel: "WhatsApp AI",
      options: [
        {
          mode: "notify",
          label: "Notify me only",
          detail:
            "Every message is recorded and staff are alerted, but the " +
            "assistant stays silent. You reply from the member's page.",
        },
        {
          mode: "booking_intent",
          label: "Answer booking questions",
          detail:
            "The assistant answers messages that read as booking or pricing " +
            "intent, and can book and take payment. Anything else alerts " +
            "staff instead.",
        },
        {
          mode: "all",
          label: "Answer everything",
          detail:
            "The assistant replies to every inbound message, with no intent " +
            "check — and acts on its own: it can book and reschedule " +
            "sessions and take payment in the chat.",
          danger: true,
        },
      ],
    },
  },
  {
    key: "gmail",
    label: "Gmail",
    pauses: "Outbound Gmail sends and the 10-minute inbound mail scan.",
    ai: {
      // New lane — off until deliberately switched on.
      defaultMode: "notify",
      // Answering by email is per-member opt-in; ACTING by email is a
      // deliberate /config Save on top (Mario, 2026-08-14).
      defaultCapability: "converse",
      capabilityOptions: [
        {
          value: "converse",
          label: "Answer only",
          detail:
            "Replies only — no bookings or payments over email. Anything it " +
            "cannot answer is handed to staff.",
        },
        {
          value: "transact",
          label: "Answer and act",
          detail:
            "The assistant can also book and reschedule sessions from an " +
            "email conversation. Payment links stay off email either way.",
        },
      ],
      memberSwitchLabel: "Email AI",
      options: [
        {
          mode: "notify",
          label: "Notify me only",
          detail:
            "Member email is recorded and shows on the member's page; the " +
            "assistant never replies.",
        },
        {
          mode: "booking_intent",
          label: "Answer booking questions",
          detail:
            "The assistant replies in-thread, from the studio mailbox, to " +
            "member email that reads as booking or pricing intent. Only " +
            "members whose Email AI switch is on are answered; everyone " +
            "else stays notify-only.",
        },
        {
          mode: "all",
          label: "Answer everything",
          detail:
            "The assistant replies in-thread to every email from a known " +
            "member whose Email AI switch is on, with no intent check. Mail " +
            "arrives on a 10-minute scan, so replies are not instant.",
          danger: true,
        },
      ],
    },
  },
  {
    key: "instagram",
    label: "Instagram",
    pauses:
      "Inbound Instagram DMs and outbound DM replies + posts. Meta's " +
      "verification handshake stays live.",
    ai: {
      defaultMode: "notify",
      defaultCapability: "transact",
      capabilityOptions: [
        {
          value: "converse",
          label: "Answer only",
          detail:
            "Replies and helps, but cannot act: no bookings, reschedules, " +
            "cancellations or payment links. It offers to hand those to the " +
            "team and alerts staff.",
        },
        {
          value: "transact",
          label: "Answer and act",
          detail:
            "Replies and acts for the member: books and reschedules " +
            "sessions and takes payment in the chat. Today's behaviour, and " +
            "the default.",
        },
      ],
      memberSwitchLabel: "Instagram AI",
      options: [
        {
          mode: "notify",
          label: "Notify me only",
          detail:
            "Every DM is recorded and staff are alerted when a new " +
            "conversation opens, but the assistant stays silent. You hand it " +
            "one person at a time from their member page.",
        },
        {
          mode: "booking_intent",
          label: "Answer booking questions",
          detail:
            "The assistant takes over DMs that read as booking or pricing " +
            "intent, and can book and take payment. Press, influencers and " +
            "partners still just alert staff.",
        },
        {
          mode: "all",
          label: "Answer everything",
          detail:
            "The assistant replies to every inbound DM, with no intent " +
            "check — and acts on its own: it can book and reschedule " +
            "sessions and take payment in the chat.",
          danger: true,
        },
      ],
    },
  },
  {
    key: "webchat",
    label: "Website chat — “Ask a question”",
    pauses:
      "The “Ask a question” chat on the booking site: the widget answers a " +
      "canned line with no assistant behind it. The site's own booking and " +
      "payment flows are separate and keep working.",
    chat: {
      defaultCapability: "transact",
      capabilityOptions: [
        {
          value: "converse",
          label: "Answer only",
          detail:
            "Answers questions and looks things up, but cannot act: no " +
            "bookings, reschedules, cancellations or payment links in the " +
            "chat. It points members at the site's booking flow or hands " +
            "them to the team.",
        },
        {
          value: "transact",
          label: "Answer and act",
          detail:
            "Answers AND acts in the chat: books and reschedules sessions " +
            "and takes payment. Today's behaviour, and the default.",
        },
      ],
      note:
        "Bookings and payments on the site itself are separate flows — they " +
        "always work and have no off switch here. These choices govern only " +
        "the “Ask a question” chat.",
    },
  },
  {
    key: "google_ads",
    label: "Google Ads",
    pauses:
      "Outbound only — the hourly upload of paid sales (a website " +
      "purchase, a website membership's first payment or a pay-link sale " +
      "that carries a Google click id; with enhanced conversions on, also " +
      "the rest and the studio's desk sales), the daily client-list sync, " +
      "and the campaign changes an approved ads card makes (a budget, " +
      "pause or enable, the max CPC cap, a negative keyword). Clavis " +
      "receives nothing from Google Ads.",
  },
];

/** The descriptor for one connector — total over {@link ConnectorKey}. */
export function connectorDescriptor(key: ConnectorKey): ConnectorDescriptor {
  const found = CONNECTOR_DESCRIPTORS.find((d) => d.key === key);
  /* c8 ignore next 3 — unreachable: the array is total over ConnectorKey and
     `connector-descriptors-total.test.ts` fails the build if it stops being. */
  if (found === undefined) {
    throw new Error(`connector_descriptor_missing: ${key}`);
  }
  return found;
}

/**
 * Per-connector settings. An object rather than a bare boolean so a future
 * per-connector option lands without a shape migration (mirrors
 * `CatalogEntryVisibility`) — which is exactly what `autonomy` did on
 * 2026-08-06.
 *
 * Why `autonomy` lives HERE and not in a top-level `whatsapp:` / `gmail:`
 * block beside the retired `instagram.autonomy`: the top-level validator is
 * not uniform. An unknown top-level key is silently DROPPED (no throw, no
 * value), while the existing `gmail:` block throws on an unknown field. A
 * mis-typed autonomy setting would therefore have saved cleanly and done
 * nothing. `connectors` rejects both unknown keys and unknown sub-fields, so
 * every mistake here is loud.
 */
export interface ConnectorSettings {
  readonly enabled?: boolean;
  /**
   * How autonomous the assistant is on this connector. Absent ⇒ the
   * connector's own {@link ConnectorAiDescriptor.defaultMode}. Meaningless
   * (and rejected at validation) on a connector with no `ai` descriptor.
   *
   * Deliberately INDEPENDENT of `enabled`: turning a connector off leaves the
   * stored mode alone, so switching it back on restores the operator's prior
   * choice rather than silently resetting.
   */
  readonly autonomy?: AiAutonomyMode;
  /**
   * What the assistant may DO once it answers on this connector — the
   * capability axis (see {@link CONNECTOR_CAPABILITIES}). Absent ⇒ the
   * connector's own {@link ConnectorAiDescriptor.defaultCapability}.
   * Independent of BOTH `enabled` and `autonomy`, with the same
   * survive-a-pause posture as `autonomy`. Meaningless (and rejected at
   * validation) on a connector with no `ai` descriptor.
   */
  readonly capability?: ConnectorCapability;
}

/**
 * Connector-keyed activation map. Only NON-default entries need be present:
 * an absent map, an absent key, or an absent flag all mean ENABLED.
 */
export type ConnectorEnablementConfig = Readonly<
  Partial<Record<ConnectorKey, ConnectorSettings>>
>;

/**
 * True unless the connector is EXPLICITLY deactivated. Default-enabled, so
 * every studio predating this field keeps working untouched; only an explicit
 * `enabled: false` pauses. Pure — the single predicate every gate (outbound
 * actions, webhook ingress, cron scanners) funnels through.
 */
export function isConnectorEnabled(
  connectors: ConnectorEnablementConfig | undefined,
  key: ConnectorKey,
): boolean {
  const entry = connectors?.[key];
  if (entry === undefined) return true;
  return entry.enabled !== false;
}

/**
 * How autonomous the assistant is on a connector, for an ALREADY-LOADED
 * config. Pure — the single predicate every routing gate funnels through, the
 * `isConnectorEnabled` posture applied to the second axis.
 *
 * Falls back to the connector's own default (NOT a shared constant) so a
 * studio that predates this field keeps its current behaviour exactly. A
 * connector with no `ai` descriptor has no ladder; `notify` is returned as the
 * conservative answer for a caller that asks anyway.
 *
 * Says NOTHING about whether the connector is on — pause is the separate,
 * higher-precedence gate. Callers check `isConnectorEnabled` first; this
 * answers only "and if it is on, how far may the assistant go?".
 */
export function resolveConnectorAutonomy(
  connectors: ConnectorEnablementConfig | undefined,
  key: ConnectorKey,
): AiAutonomyMode {
  const descriptor = connectorDescriptor(key);
  if (descriptor.ai === undefined) return "notify";
  return connectors?.[key]?.autonomy ?? descriptor.ai.defaultMode;
}

/**
 * What the assistant may DO once it answers on a connector, for an
 * ALREADY-LOADED config — `resolveConnectorAutonomy`'s sibling on the
 * capability axis.
 *
 * Falls back to the connector's own default (`transact` for the live
 * WhatsApp/Instagram/webchat lanes — a config-read blip must never strip the
 * live bot's booking tools; `converse` for gmail). Reads the default from
 * EITHER descriptor block: `ai` for the three ladder connectors, `chat` for a
 * capability-only surface like `webchat`. A connector with neither has no
 * assistant; `converse` is returned as the least-capable conservative answer
 * for a caller that asks anyway.
 */
export function resolveConnectorCapability(
  connectors: ConnectorEnablementConfig | undefined,
  key: ConnectorKey,
): ConnectorCapability {
  const defaultCapability = connectorDefaultCapability(key);
  if (defaultCapability === null) return "converse";
  return connectors?.[key]?.capability ?? defaultCapability;
}

/**
 * The connector's own default capability, or `null` when it carries no
 * assistant at all (a plain on/off connector). ONE reader for the two
 * descriptor blocks that can hold it — `ai.defaultCapability` on the ladder
 * connectors, `chat.defaultCapability` on a capability-only surface — so a
 * caller can never see one and miss the other. `resolveConnectorCapability`
 * funnels through it, and every gate and the dashboard read capability through
 * that resolver; the alternative (each site writing `d.ai?.x ?? d.chat?.x`) is
 * the hand-copied enumeration this file's own descriptor list exists to avoid.
 * (The dashboard's capability WRITER needs no default: since 2026-10-07 it
 * stores whichever value the operator picks.)
 */
export function connectorDefaultCapability(
  key: ConnectorKey,
): ConnectorCapability | null {
  const descriptor = connectorDescriptor(key);
  return (
    descriptor.ai?.defaultCapability ??
    descriptor.chat?.defaultCapability ??
    null
  );
}

/**
 * Per-member channel-AI DEFAULTS (Mario, 2026-08-14): on these channels an
 * UNSET member pill (`conversations.ai_override IS NULL` — every member
 * nobody has decided about) means the assistant does NOT answer that person,
 * whatever the studio autonomy says; staff are still alerted (one alert per
 * conversation per clock half-hour on these two channels since 2026-11-04 —
 * `studioAutonomyAlertWire` in the receiver) and the message lands on the
 * member's page. WhatsApp is deliberately
 * ABSENT: its assistant has always answered unset members, and adding it
 * here would silence the live bot for everyone whose pill was never touched.
 *
 * Explicit `'off'` stays FULLY silent (`skipPausedConversation`, unchanged)
 * and explicit `'on'` still bypasses everything — this set only decides what
 * UNSET means per channel.
 *
 * SINGLE SOURCE for both consumers: the inbound resolver's
 * `skipStudioAutonomy` gate (the behaviour) and the member-page pill's
 * effective-state rule in the dashboard (the display). A hand-copy in either
 * place is how the two would drift.
 */
export const MEMBER_AI_UNSET_DEFAULT_OFF_CHANNELS: ReadonlySet<string> =
  new Set(["instagram", "email"]);

/**
 * Map a Stripe event's own `livemode` stamp to the connector key that governs
 * it. Stripe is authoritative about which account an event came from, so
 * ingress never has to track which signing secret matched.
 */
export function stripeConnectorKey(livemode: boolean): ConnectorKey {
  return livemode ? "stripe_live" : "stripe_test";
}

/**
 * Which Stripe ACCOUNT outbound money actions (charges, refunds, Checkout
 * Sessions, subscriptions) run against. Deliberately explicit and separate
 * from the two enable flags: selection must never be INFERRED from key
 * presence or toggle state, or a rotation gap could silently redirect real
 * customer charges into the sandbox (or the reverse). Absent ⇒
 * {@link DEFAULT_STRIPE_OUTBOUND_ENVIRONMENT}.
 */
export type StripeOutboundEnvironment = "live" | "test";

/**
 * Default outbound Stripe environment when the config says nothing — the
 * sandbox, which is what E-Pulsive runs today. Moving real money is a
 * deliberate, audited `edit_studio_config` Save.
 */
export const DEFAULT_STRIPE_OUTBOUND_ENVIRONMENT: StripeOutboundEnvironment =
  "test";

/** The connector key governing a given outbound Stripe environment. */
export function stripeEnvironmentConnectorKey(
  environment: StripeOutboundEnvironment,
): ConnectorKey {
  return environment === "live" ? "stripe_live" : "stripe_test";
}

/** The slice of an L2 config that names the outbound Stripe account. */
export interface StripeOutboundEnvironmentConfigSlice {
  readonly stripe?: {
    readonly outbound_environment?: StripeOutboundEnvironment;
  };
}

/**
 * Read the outbound account out of an ALREADY-LOADED config.
 *
 * This one-liner is deliberately a shared function rather than an inlined
 * `?? DEFAULT` at each site. On 2026-07-27 a second, independent copy of
 * "which Stripe account?" charged a real card on live and then looked the
 * PaymentIntent up on sandbox. Every caller that has the config in hand reads
 * it through here; callers that must LOAD it first go through
 * `resolveStripeOutboundEnvironment` in the Stripe connector, which is built
 * on this. One rule, one expression of it.
 */
export function stripeOutboundEnvironmentOf(
  config: StripeOutboundEnvironmentConfigSlice,
): StripeOutboundEnvironment {
  return config.stripe?.outbound_environment ?? DEFAULT_STRIPE_OUTBOUND_ENVIRONMENT;
}

/** The slice of a membership offering that pins its Stripe Prices. */
export interface MembershipStripePriceSlice {
  readonly stripe_price_id?: string;
  readonly stripe_price_id_live?: string;
}

/**
 * The Stripe Price a membership sells at ON A GIVEN ACCOUNT — `undefined`
 * when that account has no Price pinned.
 *
 * A Price id is account-scoped exactly like a `client_secret`: the sandbox
 * ids E-Pulsive has always used answer `resource_missing` on the live
 * account, which fails `create_stripe_subscription`'s price-verify guard
 * BEFORE any subscription exists. So the catalog pins one id per account and
 * the selection follows `stripe.outbound_environment`, mirroring the
 * `stripe.api_key` / `stripe.api_key_live` secret split. There is
 * deliberately NO cross-environment fallback: a missing live Price must
 * surface as `membership_price_unconfigured`, never as a sandbox Price
 * silently used for real money (or the reverse).
 */
export function membershipStripePriceId(
  offering: MembershipStripePriceSlice,
  environment: StripeOutboundEnvironment,
): string | undefined {
  return environment === "live"
    ? offering.stripe_price_id_live
    : offering.stripe_price_id;
}
